import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import BottomNav from "../../components/BottomNav";

const DOC_TYPES = [
  { value: "", label: "-- Choisir --" },
  { value: "permis", label: "Permis de conduire" },
  { value: "cni", label: "Carte Nationale d'Identité" },
  { value: "passeport", label: "Passeport" },
];

const COUNTRIES = [
  { code: "CM", label: "Cameroun", currency: "XAF", locale: "fr-CM" },
  { code: "NG", label: "Nigeria", currency: "NGN", locale: "en-NG" },
  { code: "CA", label: "Canada", currency: "CAD", locale: "en-CA" },
  { code: "US", label: "USA", currency: "USD", locale: "en-US" },
];

function currencyFromCountry(code) {
  return COUNTRIES.find((c) => c.code === code)?.currency || "";
}
function localeFromCountry(code) {
  return COUNTRIES.find((c) => c.code === code)?.locale || "";
}

// Normalize backend dates for <input type="date" /> which expects YYYY-MM-DD
function toDateInputValue(v) {
  if (!v) return "";
  const s = String(v);
  // ISO: 2026-02-08T... => take first 10
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // If it's a Date string like "Sun Feb..." try parsing
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  profession: "",
  address: "",

  countryCode: "",
  displayCurrency: "",
  locale: "",

  documentType: "",
  documentNumber: "",

  dateOfBirth: "",
  documentIssueDate: "",
  documentExpiryDate: "",
};

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [original, setOriginal] = useState(emptyForm);
  const [formData, setFormData] = useState(emptyForm);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changePassword, setChangePassword] = useState(false);

  const [isEditing, setIsEditing] = useState(false);

  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);

  const [frontPreviewUrl, setFrontPreviewUrl] = useState(null);
  const [backPreviewUrl, setBackPreviewUrl] = useState(null);

  // Builds a usable URL whether backend returns filename or full URL
  const toPreviewUrl = (maybeFileOrUrl) => {
    if (!maybeFileOrUrl) return null;
    const v = String(maybeFileOrUrl);
    if (v.startsWith("http://") || v.startsWith("https://")) return v;

    const base = process.env.REACT_APP_API_URL || "";
    return `${base.replace(/\/$/, "")}/uploads/ids/${v}`;
  };

  const mapProfileResponse = useCallback((data) => {
    const user = data?.user || data || {};
    const profile = data?.profile || {};

    const mapped = {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || user.phone || "",
      profession: profile.profession || "",
      address: profile.address || "",

      countryCode: profile.countryCode || "",
      displayCurrency: profile.displayCurrency || "",
      locale: profile.locale || "",

      documentType: profile.documentType || "",
      documentNumber: profile.documentNumber || "",

      dateOfBirth: toDateInputValue(profile.dateOfBirth),
      documentIssueDate: toDateInputValue(profile.documentIssueDate),
      documentExpiryDate: toDateInputValue(profile.documentExpiryDate),
    };

    const frontUrl = profile.documentFrontUrl || profile.nationalIdUrl || null;
    const backUrl = profile.documentBackUrl || null;

    return { mapped, frontUrl, backUrl };
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/profile");
      const { mapped, frontUrl, backUrl } = mapProfileResponse(res.data);

      setOriginal(mapped);
      setFormData(mapped);

      setCurrentPassword("");
      setNewPassword("");

      setFrontFile(null);
      setBackFile(null);

      setFrontPreviewUrl(toPreviewUrl(frontUrl));
      setBackPreviewUrl(toPreviewUrl(backUrl));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Erreur lors du chargement du profil");
    } finally {
      setLoading(false);
    }
  }, [mapProfileResponse]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const canSave = useMemo(() => {
    if (!isEditing) return false;
    if (saving) return false;

    const baseOk = Boolean(
      formData.firstName?.trim() &&
        formData.lastName?.trim() &&
        formData.email?.trim() &&
        formData.phoneNumber?.trim()
    );

    // If user chose CNI, require issue/expiry dates
    const cniDatesOk =
      formData.documentType !== "cni" ||
      Boolean(formData.documentIssueDate && formData.documentExpiryDate);

    const wantsPw =
      currentPassword.trim().length > 0 || newPassword.trim().length > 0;

    const pwOk = !wantsPw || (currentPassword.trim() && newPassword.trim());

    // KYC logic
    const wantsKyc = Boolean(formData.documentType?.trim());
    const kycOk = !wantsKyc
      ? true
      : Boolean(
          formData.documentNumber?.trim() &&
            // allow existing stored images via preview URLs
            (frontFile || frontPreviewUrl) &&
            (formData.documentType !== "cni" || backFile || backPreviewUrl) &&
            cniDatesOk
        );

    return baseOk && pwOk && kycOk;
  }, [
    isEditing,
    saving,
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.phoneNumber,

    formData.documentType,
    formData.documentNumber,
    formData.documentIssueDate,
    formData.documentExpiryDate,

    formData.dateOfBirth,

    currentPassword,
    newPassword,
    frontFile,
    backFile,
    frontPreviewUrl,
    backPreviewUrl,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleCountryChange = (e) => {
    const code = e.target.value;

    setFormData((p) => ({
      ...p,
      countryCode: code,
      displayCurrency: currencyFromCountry(code) || p.displayCurrency,
      locale: localeFromCountry(code) || p.locale,
    }));
  };

  const handleDocTypeChange = (e) => {
    const value = e.target.value;

    setFormData((p) => ({
      ...p,
      documentType: value,
      ...(value !== "cni"
        ? { documentIssueDate: "", documentExpiryDate: "" }
        : {}),
    }));

    // Clean CNI-only state when leaving CNI
    if (value !== "cni") {
      setBackFile(null);
      setBackPreviewUrl(null);
    }
  };

  const validateFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formats acceptés : JPG ou PNG (images uniquement)");
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5MB)");
      return false;
    }
    return true;
  };

  const handleFrontFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!validateFile(f)) return;
    setFrontFile(f);
  };

  const handleBackFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!validateFile(f)) return;
    setBackFile(f);
  };

  const handleCancel = () => {
    setFormData(original);
    setCurrentPassword("");
    setNewPassword("");
    setFrontFile(null);
    setBackFile(null);

    // restore previews from original record
    // (original doesn't include urls; re-fetch is safest)
    fetchProfile();

    setIsEditing(false);
    setChangePassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSave) {
      toast.error("Veuillez vérifier les champs requis avant d'enregistrer.");
      return;
    }

    try {
      setSaving(true);

      const payload = new FormData();

      // User table
      payload.append("firstName", formData.firstName?.trim());
      payload.append("lastName", formData.lastName?.trim());
      payload.append("email", formData.email?.trim());
      payload.append("phoneNumber", formData.phoneNumber?.trim());

      // Profile
      payload.append("profession", formData.profession || "");
      payload.append("address", formData.address || "");

      payload.append("countryCode", formData.countryCode || "");
      payload.append("displayCurrency", formData.displayCurrency || "");
      payload.append("locale", formData.locale || "");

      payload.append("dateOfBirth", formData.dateOfBirth || "");

      // KYC (ONLY ONCE — no duplicates)
      if (formData.documentType) {
        payload.append("documentType", formData.documentType);
        payload.append("documentNumber", formData.documentNumber || "");

        if (formData.documentType === "cni") {
          payload.append("documentIssueDate", formData.documentIssueDate || "");
          payload.append("documentExpiryDate", formData.documentExpiryDate || "");
        }

        if (frontFile) payload.append("documentFront", frontFile);
        if (formData.documentType === "cni" && backFile) payload.append("documentBack", backFile);
      }

      // Password
      if (changePassword) {
        payload.append("currentPassword", currentPassword.trim());
        payload.append("newPassword", newPassword.trim());
      }

      await axios.put("/profile", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Profil mis à jour avec succès");
      setIsEditing(false);
      await fetchProfile();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Échec de la mise à jour du profil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="flex">
        <Sidebar />

        <main className="flex-1 pb-28 md:pb-8">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Mon Profil
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Consultez vos informations, puis cliquez sur “Modifier” pour les mettre à jour.
              </p>
            </div>

            <section className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-5">
              {loading ? (
                <p className="text-slate-500 dark:text-slate-400">Chargement…</p>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Top actions */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm text-slate-600 dark:text-slate-300">
                      Statut :{" "}
                      <span
                        className={`font-semibold ${
                          isEditing
                            ? "text-amber-600 dark:text-amber-300"
                            : "text-emerald-600 dark:text-emerald-300"
                        }`}
                      >
                        {isEditing ? "Modification" : "Lecture seule"}
                      </span>
                    </div>

                    {!isEditing ? (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="rounded-2xl bg-slate-900 text-white px-4 py-2.5 text-sm font-medium hover:opacity-95 transition
                                   dark:bg-white dark:text-slate-900"
                      >
                        Modifier
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleCancel}
                          className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-4 py-2.5 text-sm font-medium
                                     text-slate-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition"
                        >
                          Annuler
                        </button>
                        <button
                          type="submit"
                          disabled={!canSave}
                          title={!canSave ? "Complétez les champs requis" : "Enregistrer"}
                          className="rounded-2xl bg-slate-900 text-white px-4 py-2.5 text-sm font-medium hover:opacity-95 transition
                                     disabled:opacity-50 disabled:cursor-not-allowed dark:bg-white dark:text-slate-900"
                        >
                          {saving ? "Enregistrement…" : "Enregistrer"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Identity / basic info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field
                      label="Prénom"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      readOnly={!isEditing}
                      required
                    />
                    <Field
                      label="Nom"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      readOnly={!isEditing}
                      required
                    />
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      readOnly={!isEditing}
                      required
                    />
                    <Field
                      label="Téléphone"
                      name="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      readOnly={!isEditing}
                      required
                    />

                    {/* Country */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                        Pays
                      </label>
                      <select
                        value={formData.countryCode}
                        onChange={handleCountryChange}
                        disabled={!isEditing}
                        className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition
                          ${
                            !isEditing
                              ? "bg-slate-50 dark:bg-white/5 border-black/5 dark:border-white/10 text-slate-900 dark:text-white"
                              : "bg-white dark:bg-slate-950 border-black/10 dark:border-white/10 text-slate-900 dark:text-white focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-white/15"
                          }`}
                      >
                        <option value="">-- Choisir --</option>
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <Field
                      label="Date de naissance"
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      readOnly={!isEditing}
                    />

                    <Field
                      label="Profession"
                      name="profession"
                      value={formData.profession}
                      onChange={handleChange}
                      readOnly={!isEditing}
                    />

                    <div className="space-y-1 sm:col-span-2">
                      <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                        Adresse
                      </label>
                      <textarea
                        name="address"
                        rows={3}
                        value={formData.address}
                        onChange={handleChange}
                        readOnly={!isEditing}
                        className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition
                          ${
                            !isEditing
                              ? "bg-slate-50 dark:bg-white/5 border-black/5 dark:border-white/10 text-slate-900 dark:text-white"
                              : "bg-white dark:bg-slate-950 border-black/10 dark:border-white/10 text-slate-900 dark:text-white focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-white/15"
                          }`}
                      />
                    </div>
                  </div>

                  {/* KYC */}
                  <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                        Vérification d'identité
                      </h3>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        (Optionnel)
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Type de document
                        </label>
                        <select
                          name="documentType"
                          value={formData.documentType}
                          onChange={handleDocTypeChange}
                          disabled={!isEditing}
                          className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition
                            ${
                              !isEditing
                                ? "bg-slate-50 dark:bg-white/5 border-black/5 dark:border-white/10 text-slate-900 dark:text-white"
                                : "bg-white dark:bg-slate-950 border-black/10 dark:border-white/10 text-slate-900 dark:text-white focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-white/15"
                            }`}
                        >
                          {DOC_TYPES.map((d) => (
                            <option key={d.value} value={d.value}>
                              {d.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <Field
                        label="Numéro du document"
                        name="documentNumber"
                        value={formData.documentNumber}
                        onChange={handleChange}
                        readOnly={!isEditing}
                        placeholder="Ex: CNI1234567"
                        required={Boolean(isEditing && formData.documentType)}
                      />
                    </div>

                    {formData.documentType && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field
                          label="Date de délivrance"
                          name="documentIssueDate"
                          type="date"
                          value={formData.documentIssueDate}
                          onChange={handleChange}
                          readOnly={!isEditing}
                          required={Boolean(isEditing && formData.documentType)}
                        />
                        <Field
                          label="Date d'expiration"
                          name="documentExpiryDate"
                          type="date"
                          value={formData.documentExpiryDate}
                          onChange={handleChange}
                          readOnly={!isEditing}
                          required={Boolean(isEditing && formData.documentType)}
                        />
                      </div>
                    )}

                    {/* Recto / Verso */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Document (Recto)
                          {formData.documentType ? (
                            <span className="text-rose-600 ml-1">*</span>
                          ) : null}
                        </label>

                        {frontPreviewUrl ? (
                          <img
                            src={frontPreviewUrl}
                            alt="Document recto"
                            className="h-36 w-auto rounded-2xl border border-black/10 dark:border-white/10 object-cover bg-white"
                          />
                        ) : (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            Aucun fichier
                          </p>
                        )}

                        {isEditing && formData.documentType && (
                          <input
                            type="file"
                            onChange={handleFrontFileChange}
                            accept=".jpg,.jpeg,.png"
                            className="w-full text-sm"
                          />
                        )}

                        {frontFile && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Sélectionné :{" "}
                            <span className="font-medium">{frontFile.name}</span>
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Document (Verso)
                          {formData.documentType === "cni" ? (
                            <span className="ml-1 text-rose-600 dark:text-rose-300">
                              *
                            </span>
                          ) : null}
                        </label>

                        {backPreviewUrl ? (
                          <img
                            src={backPreviewUrl}
                            alt="Document verso"
                            className="h-36 w-auto rounded-2xl border border-black/10 dark:border-white/10 object-cover bg-white"
                          />
                        ) : (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            Aucun fichier
                          </p>
                        )}

                        {isEditing && formData.documentType === "cni" && (
                          <input
                            type="file"
                            onChange={handleBackFileChange}
                            accept=".jpg,.jpeg,.png"
                            className="w-full text-sm"
                          />
                        )}

                        {backFile && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Sélectionné :{" "}
                            <span className="font-medium">{backFile.name}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {isEditing && formData.documentType && !canSave && (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Pour enregistrer ce document : renseignez le numéro et joignez le recto
                        {formData.documentType === "cni"
                          ? " + le verso + les dates (délivrance/expiration)"
                          : ""}
                        .
                      </p>
                    )}
                  </div>

                  {/* Password only in edit mode */}
                  {isEditing && (
                    <div className="space-y-3">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <input
                          type="checkbox"
                          checked={changePassword}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setChangePassword(v);
                            if (!v) {
                              setCurrentPassword("");
                              setNewPassword("");
                            }
                          }}
                        />
                        Changer le mot de passe
                      </label>

                      {changePassword && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                              Mot de passe actuel
                            </label>
                            <input
                              type="password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              autoComplete="current-password"
                              className="w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-4 py-3 text-sm text-slate-900 dark:text-white outline-none"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                              Nouveau mot de passe
                            </label>
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              autoComplete="new-password"
                              className="w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-4 py-3 text-sm text-slate-900 dark:text-white outline-none"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Global hint */}
                  {isEditing && !canSave && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Champs requis : Prénom, Nom, Email, Téléphone. Si changement de mot de passe :
                      remplissez les deux champs. Si CNI : dates + recto + verso.
                    </p>
                  )}
                </form>
              )}
            </section>
          </div>

          <div className="md:hidden fixed bottom-0 left-0 w-full z-50">
            <BottomNav />
          </div>
        </main>
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  placeholder,
  value,
  onChange,
  readOnly,
  required,
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
        {label}{" "}
        {required ? <span className="text-rose-600 dark:text-rose-300">*</span> : null}
      </label>
      <input
        name={name}
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition
          ${
            readOnly
              ? "bg-slate-50 dark:bg-white/5 border-black/5 dark:border-white/10 text-slate-900 dark:text-white"
              : "bg-white dark:bg-slate-950 border-black/10 dark:border-white/10 text-slate-900 dark:text-white focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-white/15"
          }`}
      />
    </div>
  );
}
